import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    sm: { boxSize: tokens.globals["spacing-4"], fontSize: tokens.typography.fontSize["sm-10px"], labelFontSize: tokens.typography.fontSize["lg-14px"] },
    md: { boxSize: tokens.globals["spacing-5"], fontSize: tokens.typography.fontSize["md-12px"], labelFontSize: tokens.typography.fontSize["lg-14px"] },
    lg: { boxSize: tokens.globals["spacing-6"], fontSize: tokens.typography.fontSize["lg-14px"], labelFontSize: tokens.typography.fontSize["lg-14px"] },
};

const StyledWrapper = styled.label(({ $disabled = false }) => ({
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.5 : 1,

    "&:focus-within": {
        "& > span:first-of-type": {
            outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.ui.ring}`,
            outlineOffset: tokens.globals["spacing-0-5"],
        },
    },
}));

const StyledInput = styled.input({
    position: "absolute",
    width: tokens.globals["spacing-px"],
    height: tokens.globals["spacing-px"],
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
});

const StyledBox = styled.span(({ $size = "sm", $checked = false, $indeterminate = false }) => {
    const config = sizes[$size] || sizes.sm;

    return {
        width: config.boxSize,
        height: config.boxSize,
        borderRadius: "4px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: tokens.effects.effects.shadow.default,
        background: ($checked || $indeterminate)
            ? tokens.colors.theme["button-color"]
            : tokens.colors.surfaces["container-background"],
        border: `${tokens.globals["spacing-px"]} solid ${($checked || $indeterminate) ? tokens.colors.theme["button-color"] : tokens.colors.ui.input}`,
        transition: "all 0.15s ease-in-out",
        pointerEvents: "none",
        flexShrink: 0,
    };
});

const StyledIcon = styled.i(({ $size = "sm" }) => ({
    color: tokens.colors.neutral.white,
    fontSize: (sizes[$size] || sizes.sm).fontSize,
    lineHeight: "1",
}));

const StyledLabel = styled.span(({ $size = "sm" }) => ({
    color: tokens.colors.text.primary,
    fontSize: (sizes[$size] || sizes.sm).labelFontSize,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "20px",
}));

/**
 * Accessible AIE checkbox. Renders a visually-hidden native `<input type="checkbox">`
 * under the custom box so screen readers and keyboard focus work correctly.
 * @param {boolean} [props.checked] Controlled checked state.
 * @param {boolean} [props.indeterminate] Synced onto the native input's `indeterminate`
 *   DOM property (not a real HTML attribute).
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} [props.onChange] Change handler.
 * Also: size, disabled, name, id, label, ariaLabel, disabledReason (self-explanatory).
 */
const Checkbox = forwardRef(function Checkbox(
    {
        checked = false,
        indeterminate = false,
        size = "sm",
        disabled = false,
        onChange,
        name,
        id,
        label,
        ariaLabel,
        disabledReason,
        ...props
    },
    ref
) {
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = Boolean(indeterminate);
        }
    }, [indeterminate]);

    return (
        <StyledWrapper $disabled={disabled} title={disabled ? disabledReason : undefined}>
            <StyledInput
                ref={inputRef}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                name={name}
                id={id}
                aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
                aria-checked={indeterminate ? "mixed" : checked}
                onChange={onChange}
                {...props}
            />
            <StyledBox $size={size} $checked={checked} $indeterminate={indeterminate} aria-hidden="true">
                {checked && !indeterminate && <StyledIcon $size={size} className="fas fa-check" />}
                {indeterminate && <StyledIcon $size={size} className="fas fa-minus" />}
            </StyledBox>
            {label && <StyledLabel $size={size}>{label}</StyledLabel>}
        </StyledWrapper>
    );
});

Checkbox.displayName = "Checkbox";

export default Checkbox;
